// App-side delegate for `@heidi/commerce/data` (D2 batch 3, ARCFE-976).
//
// Three things stayed here on purpose: the team-role read (workspace door),
// the `sleep(1500)` that waits for the Stripe webhook before invalidating —
// invalidate sooner and the refetch returns the pre-upgrade billing — and the
// `is_stripe_managed` conditional, which sits beside the invalidators it
// chooses between.
import { usePaywallStates } from '@/atoms/paywallAtoms';
import { sleep } from '@/lib/utils';
import { logger } from '@/utils/logger/logger';
import { useBillingErrorToast } from '@heidi/commerce';
import { useMutateUpgradePlan as useMutateUpgradePlanCore } from '@heidi/commerce/data';
import { toast } from '@heidi/design-system/toast';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useIntl } from 'react-intl';
import { invalidateCurrentTeamBillingInfo } from '../query/useQueryTeamBilling';
import { invalidateCurrentUser } from '../query/useQueryUser';
import { invalidateCurrentUserBillingInfo } from '../query/useQueryUserBillingInfo';

export function useMutateUpgradePlan() {
  const intl = useIntl();
  const showBillingError = useBillingErrorToast();
  const { toggleUpgradeTogetherModal } = usePaywallStates();
  const { isNonOwnerTeamMember } = useCombinedQueriesTeamGeneralInfo();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();

  return useMutateUpgradePlanCore({
    canUpgrade: !isNonOwnerTeamMember,
    onUpgradeBlocked: () => {
      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          defaultMessage: 'You can’t upgrade to Pro Plus',
          id: 'jdKptNpgnx',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Please contact your team owner to upgrade the plan.',
          id: 'u+SudVB9uJ',
        }),
      });
    },
    onUpgradeSuccess: async () => {
      await sleep(1500);
      await invalidateCurrentUser();
      await invalidateCurrentUserBillingInfo();
      if (teamDetails?.team.is_stripe_managed) {
        await invalidateCurrentTeamBillingInfo(teamDetails?.team.id);
      }

      toast({
        tone: 'success',
        title: intl.formatMessage({
          defaultMessage: 'Upgraded successfully',
          id: 'VAq4Rdbvh7',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Your plan has been upgraded.',
          id: 'n7gvWXRrYn',
        }),
      });
      toggleUpgradeTogetherModal(false);
    },
    onUpgradeError: (error) => {
      logger.error('Error:', error);
      showBillingError({
        title: intl.formatMessage({
          defaultMessage: 'Couldn’t upgrade your plan',
          id: 'P4KGnw+HFh',
        }),
      });
    },
  });
}
